import React, { useState } from "react";
import { Loader2, Mail, Eye, BellRing } from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const REMINDER_KIND_LABELS = {
  quote_not_viewed: "Devis non consulté",
  quote_viewed_not_accepted: "Devis consulté non accepté",
  quote_expiring: "Devis bientôt expiré",
  invoice_due_soon: "Facture bientôt échue",
  invoice_overdue: "Facture impayée",
};

const ReminderPanel = ({ docType, doc, types, onSent }) => {
  const [type, setType] = useState(types[0]?.value || "");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);

  const doPreview = async () => {
    if (!type) return;
    setLoadingPreview(true);
    try {
      const { data } = await API.get("/reminders/preview", {
        params: { doc_type: docType, id: doc.id, type },
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const send = async () => {
    if (!type) return;
    setSending(true);
    try {
      await API.post(`/reminders/${docType}/${doc.id}`, { type });
      toast.success("Relance envoyée");
      setPreview(null);
      onSent?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const reminders = doc.reminders || [];

  return (
    <div className="bg-amber-50 rounded-lg p-4 space-y-3" data-testid="reminder-panel">
      <p className="font-semibold text-sm text-slate-800 flex items-center gap-2">
        <BellRing className="w-4 h-4" /> Relance client
      </p>
      <div className="space-y-2">
        <Label className="text-xs">Type de relance</Label>
        <div className="flex gap-2 flex-wrap">
          <Select value={type} onValueChange={(v) => { setType(v); setPreview(null); }}>
            <SelectTrigger className="w-64" data-testid="reminder-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {types.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={doPreview} disabled={loadingPreview} data-testid="reminder-preview-button">
            {loadingPreview ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
            Aperçu
          </Button>
          <Button size="sm" onClick={send} disabled={sending} data-testid="reminder-send-button">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Envoyer la relance
          </Button>
        </div>
      </div>

      {preview && (
        <div className="bg-white rounded border p-3 text-xs" data-testid="reminder-preview">
          <p className="text-muted-foreground mb-1">À : {preview.recipient || "—"}</p>
          <p className="font-semibold mb-2">{preview.subject}</p>
          <div className="border-t pt-2" dangerouslySetInnerHTML={{ __html: preview.html }} />
        </div>
      )}

      {reminders.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-700 mt-2 mb-1">Relances envoyées</p>
          <div className="space-y-1">
            {reminders.slice().reverse().map((r, i) => (
              <div key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                <span>{(r.at || "").slice(0, 16).replace("T", " ")}</span>
                <span className="text-slate-700">{REMINDER_KIND_LABELS[r.kind] || r.kind}</span>
                <span>→ {r.to}</span>
                {r.status === "failed" && <span className="text-red-500">(échec)</span>}
                {r.auto === false && <span className="text-[10px] bg-slate-200 px-1 rounded">manuel</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReminderPanel;
